import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { adminUpdateProduct, adminUploadImage, adminDeleteProduct, API } from "../lib/api";
import { useAdmin } from "../lib/admin";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Star, Trash2, Upload } from "lucide-react";

const LENGTHS = ['8"', '10"', '12"', '14"', '16"', '18"', '20"', '22"', '24"', '26"', '28"', '30"'];
const COLORS = ["Auburn", "Blonde", "Burgundy", "Copper", "Dark Brown", "Honey Blonde", "Jet Black", "Natural Black", "Platinum"];
const LACE_TYPES = ["5x5", "13x4", "13x6", "360", "HD Lace"];

const EditProductDialog = ({ product, open, onOpenChange, onSaved, onDeleted }) => {
  const { password } = useAdmin();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product) {
      setForm({
        title: product.title || "",
        description: product.description || "",
        price: product.price ?? "",
        compare_at_price: product.compare_at_price ?? "",
        image: product.image || "",
        images: Array.isArray(product.images) && product.images.length > 0
          ? [...product.images]
          : (product.image ? [product.image] : []),
        length: product.length || "",
        color: product.color || "",
        lace_type: product.lace_type || "",
        category: product.category || "Wig",
        shopify_url: product.shopify_url || "",
        featured: !!product.featured,
        available: product.available !== false,
        stock: product.stock ?? "",
      });
    }
  }, [product]);

  if (!product || !form) return null;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Convert relative /api/static/... to absolute URL for previews
  const previewSrc = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        price: parseFloat(form.price),
        compare_at_price: form.compare_at_price === "" ? null : parseFloat(form.compare_at_price),
        image: form.image,
        images: form.images,
        length: form.length,
        color: form.color,
        lace_type: form.lace_type,
        category: form.category,
        shopify_url: form.shopify_url,
        featured: form.featured,
        available: form.available,
        stock: form.stock === "" ? null : parseInt(form.stock, 10),
      };
      const updated = await adminUpdateProduct(product.handle, payload, password);
      toast.success("Saved");
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleFilesPicked = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const url = await adminUploadImage(f, password);
        uploaded.push(url);
      }
      setForm((p) => {
        const newImages = [...(p.images || []), ...uploaded];
        return {
          ...p,
          images: newImages,
          image: p.image || newImages[0] || "",
        };
      });
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const setAsCover = (idx) => {
    setForm((p) => {
      const imgs = [...p.images];
      const [picked] = imgs.splice(idx, 1);
      imgs.unshift(picked);
      return { ...p, images: imgs, image: picked };
    });
  };

  const removeImage = (idx) => {
    setForm((p) => {
      const imgs = p.images.filter((_, i) => i !== idx);
      const wasCover = p.images[idx] === p.image;
      return { ...p, images: imgs, image: wasCover ? (imgs[0] || "") : p.image };
    });
  };

  const moveImage = (idx, dir) => {
    setForm((p) => {
      const imgs = [...p.images];
      const j = idx + dir;
      if (j < 0 || j >= imgs.length) return p;
      [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
      return { ...p, images: imgs, image: imgs[0] };
    });
  };

  const addImageByUrl = () => {
    const url = window.prompt("Paste image URL");
    if (!url) return;
    setForm((p) => {
      const newImages = [...(p.images || []), url];
      return { ...p, images: newImages, image: p.image || url };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="edit-product-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Edit product</DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-widest">
            {product.handle}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {/* Image management */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest">
                Images ({form.images.length})
              </Label>
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  multiple
                  className="hidden"
                  data-testid="edit-image-file-input"
                  onChange={(e) => handleFilesPicked(Array.from(e.target.files || []))}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="edit-image-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {uploading ? "Uploading…" : "Upload"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="rounded-sm text-xs"
                  onClick={addImageByUrl}
                  data-testid="edit-image-add-url-btn"
                >
                  + URL
                </Button>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground mb-3">
              The first image is the <strong>cover</strong> (shown on the product card &amp; homepage).
              Tap <Star className="inline w-3 h-3" /> on any image to set it as cover.
              Pick multiple files at once to upload a whole slideshow.
            </p>

            {form.images.length === 0 ? (
              <div className="border-2 border-dashed border-border/60 rounded-sm py-8 text-center">
                <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No images yet. Click <strong>Upload</strong> to add some.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {form.images.map((url, idx) => {
                  const isCover = idx === 0;
                  return (
                    <div
                      key={`${url}-${idx}`}
                      className={`relative group aspect-[4/5] bg-secondary rounded-sm overflow-hidden border-2 ${
                        isCover ? "border-[hsl(var(--primary))]" : "border-transparent"
                      }`}
                      data-testid={`edit-image-thumb-${idx}`}
                    >
                      <img
                        src={previewSrc(url)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {isCover && (
                        <div className="absolute top-1 left-1 bg-[hsl(var(--primary))] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm">
                          Cover
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5">
                        {!isCover && (
                          <button
                            type="button"
                            onClick={() => setAsCover(idx)}
                            className="text-white text-[10px] uppercase tracking-wider px-2 py-1 bg-[hsl(var(--primary))] rounded-sm flex items-center gap-1"
                            data-testid={`edit-image-set-cover-${idx}`}
                          >
                            <Star className="w-3 h-3" /> Cover
                          </button>
                        )}
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => moveImage(idx, -1)}
                            disabled={idx === 0}
                            className="bg-white/90 hover:bg-white text-black p-1 rounded-sm disabled:opacity-30"
                            data-testid={`edit-image-up-${idx}`}
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(idx, 1)}
                            disabled={idx === form.images.length - 1}
                            className="bg-white/90 hover:bg-white text-black p-1 rounded-sm disabled:opacity-30"
                            data-testid={`edit-image-down-${idx}`}
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-sm"
                            data-testid={`edit-image-remove-${idx}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Title</Label>
            <Input
              data-testid="edit-title"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              className="mt-1.5 rounded-sm"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Price ($)</Label>
              <Input
                data-testid="edit-price"
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setField("price", e.target.value)}
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Compare-at price (sale)</Label>
              <Input
                data-testid="edit-compare-price"
                type="number"
                step="0.01"
                value={form.compare_at_price}
                onChange={(e) => setField("compare_at_price", e.target.value)}
                placeholder="Leave empty if not on sale"
                className="mt-1.5 rounded-sm"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Stock count</Label>
            <Input
              data-testid="edit-stock"
              type="number"
              min="0"
              step="1"
              value={form.stock}
              onChange={(e) => setField("stock", e.target.value)}
              placeholder="Leave empty to hide stock counter"
              className="mt-1.5 rounded-sm"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Optional. Shown to customers as "X in stock" or "Only N left" when ≤ 5.
            </p>
          </div>

          {/* Section / Category picker */}
          <div>
            <Label className="text-xs uppercase tracking-widest">Section</Label>
            <Select value={form.category} onValueChange={(v) => setField("category", v)}>
              <SelectTrigger data-testid="edit-category" className="mt-1.5 rounded-sm">
                <SelectValue placeholder="Wig" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Wig">All Wigs</SelectItem>
                <SelectItem value="Pixie">Pixie Cuts</SelectItem>
                <SelectItem value="Weave">Weaves</SelectItem>
                <SelectItem value="Hair Care">Hair Care</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Choose which section on the homepage this product should appear in.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Length</Label>
              <Select value={form.length} onValueChange={(v) => setField("length", v)}>
                <SelectTrigger data-testid="edit-length" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {LENGTHS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Color</Label>
              <Select value={form.color} onValueChange={(v) => setField("color", v)}>
                <SelectTrigger data-testid="edit-color" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Lace Type</Label>
              <Select value={form.lace_type} onValueChange={(v) => setField("lace_type", v)}>
                <SelectTrigger data-testid="edit-lace" className="mt-1.5 rounded-sm">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {LACE_TYPES.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Description</Label>
            <Textarea
              data-testid="edit-description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="mt-1.5 rounded-sm"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Shopify product URL</Label>
            <Input
              data-testid="edit-shopify-url"
              value={form.shopify_url}
              onChange={(e) => setField("shopify_url", e.target.value)}
              className="mt-1.5 rounded-sm"
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              The page customers go to when they click "Shop on Kay's".
            </p>
          </div>

          <div className="flex items-center justify-between border-t border-border/60 pt-4">
            <div className="flex items-center gap-3">
              <Switch
                data-testid="edit-featured"
                checked={form.featured}
                onCheckedChange={(v) => setField("featured", v)}
              />
              <Label className="text-sm">Featured (shows first)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="edit-available"
                checked={form.available}
                onCheckedChange={(v) => setField("available", v)}
              />
              <Label className="text-sm">Available</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="edit-delete-btn"
            variant="ghost"
            onClick={async () => {
              if (!window.confirm(`Delete "${product.title}"? This can't be undone.`)) return;
              try {
                await adminDeleteProduct(product.handle, password);
                toast.success("Product deleted");
                onDeleted?.(product);
                onOpenChange(false);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't delete");
              }
            }}
            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 mr-auto"
            disabled={saving}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-testid="edit-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditProductDialog;
